import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useConfig } from '@/composables/config'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { generateNonce } from '@/utils/crypto'
import { loadScript } from '@/utils/loadScript'

declare global {
    interface Window {
        AppleID?: {
            auth: {
                init: (config: AppleSignInConfig) => void
                signIn: () => Promise<AppleSignInResponse>
            }
        }
    }
}

interface AppleSignInConfig {
    clientId: string
    scope: string
    redirectURI: string
    nonce: string
    state: string
    usePopup: boolean
}

interface AppleSignInResponse {
    authorization: {
        code: string
        id_token: string
        state: string
    }
    user?: {
        name?: {
            firstName?: string
            middleName?: string
            lastName?: string
        }
        email?: string
    }
}

export const useAppleAuthStore = defineStore('appleAuth', () => {
    const userStore = useUserStore()
    const loggerStore = useLoggerStore()

    const { VUE_APP_APPLE_CLIENT_ID, VUE_APP_APPLE_REDIRECT } = useConfig()

    const initialized = ref(false)
    const isSigningIn = ref(false)
    const nonce = ref<{ rawNonce: string; hashedNonce: string }>()
    const state = ref<string>()

    async function initGlobalSignIn() {
        if (initialized.value) {
            return
        }

        const result = await loadScript({
            src: 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
            scriptName: 'Apple Sign-In',
            useExisting: true,
        })

        if (result.status !== 'ok') {
            return
        }

        try {
            nonce.value = await generateNonce()
            state.value = crypto.randomUUID()

            window.AppleID?.auth.init({
                clientId: VUE_APP_APPLE_CLIENT_ID,
                scope: 'name email',
                redirectURI: VUE_APP_APPLE_REDIRECT,
                nonce: nonce.value.hashedNonce,
                state: state.value,
                usePopup: true,
            })
            initialized.value = true
        } catch (error) {
            console.error('Apple auth initialization error:', error)
        }
    }

    async function signIn(onFinish: (isSuccess: boolean, errorMessage?: string) => void) {
        if (!window.AppleID || !initialized.value || isSigningIn.value) {
            return
        }

        isSigningIn.value = true
        let errorMessage: string | undefined

        try {
            const response = await window.AppleID.auth.signIn()

            if (response.authorization.state !== state.value) {
                throw new Error('Invalid state returned from Apple sign-in')
            }

            const res = await fetch(VUE_APP_APPLE_REDIRECT, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    credential: response.authorization.id_token,
                    user: response.user,
                    nonce: nonce.value?.rawNonce,
                }),
            })

            if (res.status > 399) {
                const parsed = await res.json()
                errorMessage = parsed.message
            }
        } catch (e) {
            errorMessage = (e as Error).message || (e as { error: string }).error

            if (['popup_closed_by_user', 'user_cancelled_authorize'].includes(errorMessage)) {
                // we don't treat user cancellation as an error
                return
            }
        } finally {
            isSigningIn.value = false
        }

        await userStore.fetchUserInfo()

        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'login',
            action: userStore.isAuthorized ? 'success' : 'fail',
        })

        onFinish(userStore.isAuthorized, errorMessage)
    }

    return {
        initGlobalSignIn,
        signIn,
        initialized,
        isSigningIn,
    }
})
